import { decodeSectionAnswers } from './answer-codecs'
import type { DraftAnswerMap, DraftAnswerValue } from './answer-model'
import type { RunnerDefinition, ServerSectionState } from './model'

/**
 * Draft autosave and resume (grading robustness, owner rule 5, 17 Oct 2026). Pure helpers: no React, no network, no storage.
 *
 * A draft is the answers typed so far in the section being sat, saved to the server while the student works
 * (`PATCH test-runs/<id>/update/` with `action: "save_draft"`, in the `submit_section` shape) and, as the runner's own answer map,
 * to this browser as a fallback. It changes nothing about
 * the timer, the submit or the section navigation: it only lets a refresh, a closed tab or a dropped connection resume with
 * the answers that were typed, instead of abandoning the run and losing them.
 */

/** Seconds of quiet after the last keystroke before the draft goes to the server. */
export const DRAFT_DEBOUNCE_MS = 3_000
/** The longest a continuously typing student goes without a server save. */
export const DRAFT_MAX_WAIT_MS = 15_000
/** How soon the browser copy follows a change. It is a synchronous write, so it is cheap. */
export const LOCAL_DRAFT_DEBOUNCE_MS = 400
/** A browser copy older than this is never offered back (a shared computer must not hand over yesterday's essay). */
export const LOCAL_DRAFT_MAX_AGE_MS = 24 * 60 * 60 * 1000
/**
 * `keepalive` fetches share a 64 KiB budget across the page, so a save on hide or unload is only attempted when the body is
 * well inside it. The debounced saves, which are ordinary requests, carry the rest.
 */
export const KEEPALIVE_MAX_BYTES = 60_000

export const LOCAL_DRAFT_VERSION = 1 as const

/** Every browser copy of a draft starts with this, so sign-out can remove them all without knowing whose they were. */
export const LOCAL_DRAFT_KEY_PREFIX = 'ielts-v2-runner-draft:'

/**
 * The browser copy is keyed by the signed-in student as well as the mode and the test: on a shared school computer the next
 * student to open the same test must neither be offered nor be able to read the previous student's essay (adversarial review
 * #18). Without a user id (preview, a test double) the key is the older mode-and-test one.
 */
export function localDraftKey(mode: string, testId: string, userId?: string | null): string {
  return userId ? `${LOCAL_DRAFT_KEY_PREFIX}${mode}:${userId}:${testId}` : `${LOCAL_DRAFT_KEY_PREFIX}${mode}:${testId}`
}

export interface LocalDraft {
  version: typeof LOCAL_DRAFT_VERSION
  runId: string
  sectionId: string
  savedAtMs: number
  answers: DraftAnswerMap
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
}

function isDraftValue(value: unknown): value is DraftAnswerValue {
  return typeof value === 'string' || (Array.isArray(value) && value.every((item) => typeof item === 'string'))
}

/**
 * A plain answer map, or undefined when it is missing or not one. Anything that is not a string or a list of strings is dropped
 * value by value, so one bad entry cannot cost the student the rest. Used to validate this browser's copy.
 */
export function parseDraftState(raw: unknown): DraftAnswerMap | undefined {
  if (!isRecord(raw)) return undefined
  const entries = Object.entries(raw).filter((entry): entry is [string, DraftAnswerValue] => isDraftValue(entry[1]))
  if (entries.length === 0) return undefined
  return Object.fromEntries(entries) as DraftAnswerMap
}

export function serializeLocalDraft(draft: LocalDraft): string {
  return JSON.stringify(draft)
}

/** A stored browser draft, or null when it is missing, unreadable, from another version, or too old. */
export function readLocalDraft(raw: string | null | undefined, nowMs: number): LocalDraft | null {
  if (!raw) return null
  let parsed: unknown
  try {
    parsed = JSON.parse(raw)
  } catch {
    return null
  }
  if (!isRecord(parsed) || parsed.version !== LOCAL_DRAFT_VERSION) return null
  if (typeof parsed.runId !== 'string' || !parsed.runId || typeof parsed.sectionId !== 'string' || !parsed.sectionId) return null
  if (typeof parsed.savedAtMs !== 'number' || !Number.isFinite(parsed.savedAtMs)) return null
  if (nowMs - parsed.savedAtMs > LOCAL_DRAFT_MAX_AGE_MS || parsed.savedAtMs > nowMs + 5 * 60 * 1000) return null
  const answers = parseDraftState(parsed.answers)
  if (!answers) return null
  return { version: LOCAL_DRAFT_VERSION, runId: parsed.runId, sectionId: parsed.sectionId, savedAtMs: parsed.savedAtMs, answers }
}

/** True when the server holds a non-empty draft for this section (for the "your answers were saved" line). */
export function sectionHasDraft(section: ServerSectionState): boolean {
  return section.status === 'in_progress'
    && ((section.draftAnswers !== undefined && Object.keys(section.draftAnswers).length > 0)
      || (section.draftEncoded !== undefined && Object.keys(section.draftEncoded).length > 0))
}

/**
 * Fills `draftAnswers` from the server's encoded draft, using the authored definition to name each answer. A section that
 * already carries `draftAnswers`, has no draft, is not in progress, or is not in the definition is returned untouched.
 */
export function withDecodedDrafts(
  serverSections: readonly ServerSectionState[],
  definition: RunnerDefinition | undefined,
): readonly ServerSectionState[] {
  if (!definition) return serverSections
  return serverSections.map((section) => {
    if (section.status !== 'in_progress' || section.draftAnswers || !section.draftEncoded) return section
    const authored = definition.sections.find((candidate) => candidate.id === section.sectionId)
    if (!authored) return section
    const draftAnswers = decodeSectionAnswers(authored, section.draftEncoded)
    return Object.keys(draftAnswers).length > 0 ? { ...section, draftAnswers } : section
  })
}

/**
 * Lays a browser draft over the server's section list when it is the newer copy: the same run, a section the server still
 * holds as in progress, and a save time after the server's own draft (or the server has none). The result is what
 * `RUN_READY` reconciles, so the answers reach the section exactly as a server draft would. Everything else is returned as is.
 */
export function mergeLocalDraft(
  serverSections: readonly ServerSectionState[],
  draft: LocalDraft | null,
  runId: string,
  definition?: RunnerDefinition,
): readonly ServerSectionState[] {
  if (!draft || draft.runId !== runId) return serverSections
  return withDecodedDrafts(serverSections, definition).map((section) => {
    if (section.sectionId !== draft.sectionId || section.status !== 'in_progress') return section
    if (section.draftSavedAtMs !== undefined && section.draftSavedAtMs >= draft.savedAtMs) return section
    return { ...section, draftAnswers: { ...section.draftAnswers, ...draft.answers }, draftSavedAtMs: draft.savedAtMs }
  })
}

/** True when the JSON body of a keepalive request fits the shared budget. */
export function fitsKeepalive(body: string): boolean {
  // UTF-8 length: an essay in Arabic is two bytes a letter, not one.
  return new TextEncoder().encode(body).length <= KEEPALIVE_MAX_BYTES
}
